'use client';

import React, { useState } from 'react';
import {
  FlaskConical,
  Users,
  Calendar,
  CheckCircle,
  ArrowRight,
  Dog,
  Clock,
  BookOpen,
  Globe,
  ChevronDown,
  ChevronUp,
  Star,
  Shield,
  Stethoscope,
} from 'lucide-react';
import ClinigolLogo from '@/components/ui/ClinigolLogo';
import Link from 'next/link';
import RequestToJoinModal from './RequestToJoinModal';

interface EligibilityItem {
  id: string;
  text: string;
  met: boolean;
}

interface ReceiveItem {
  id: string;
  icon: React.ElementType;
  text: string;
}

interface TimelineItem {
  id: string;
  week: string;
  label: string;
  desc: string;
}

interface Trial {
  id: string;
  code: string;
  name: string;
  tagline: string;
  status: string;
  phase: string;
  enrolled: number;
  capacity: number;
  duration: string;
  startDate: string;
  endDate: string;
  sponsor: string;
  leadVet: string;
  location: string;
  description: string;
  eligibility: EligibilityItem[];
  whatYoullReceive: ReceiveItem[];
  timeline: TimelineItem[];
  color: string;
  spots: number;
}

const trials: Trial[] = [
  {
    id: 'trial-mfy-001',
    code: 'MFY-001',
    name: 'Mindful Years',
    tagline: 'A longitudinal study into nutritional support for canine cognitive ageing',
    status: 'recruiting',
    phase: 'Phase 2',
    enrolled: 54,
    capacity: 60,
    duration: '8 weeks',
    startDate: 'February 2026',
    endDate: 'June 2026',
    sponsor: 'Clinigol Nutrition Solutions',
    leadVet: 'Dr. James Hartley, MRCVS',
    location: 'UK-wide (remote participation)',
    description: 'Mindful Years investigates whether a proprietary blend of CNS active ingredients — delivered as a daily supplement — can meaningfully improve cognitive function scores in dogs aged 8 and over. Participants complete structured behavioural assessments at baseline, 4 weeks, and 8 weeks.',
    eligibility: [
      { id: 'elig-mfy-age', text: 'Dog aged 8 years or older', met: true },
      { id: 'elig-mfy-breed', text: 'Any breed — no exclusions', met: true },
      { id: 'elig-mfy-health', text: 'No active serious illness or current medication for cognitive conditions', met: true },
      { id: 'elig-mfy-vet', text: 'Must be registered with a participating Clinigol vet', met: true },
      { id: 'elig-mfy-commit', text: 'Able to commit to 3 assessments over 8 weeks', met: true },
      { id: 'elig-mfy-consent', text: 'Owner aged 18 or over and able to provide informed consent', met: true },
    ],
    whatYoullReceive: [
      { id: 'receive-mfy-supp', icon: Star, text: '8-week supply of Clinigol CNS supplement (complimentary)' },
      { id: 'receive-mfy-guide', icon: BookOpen, text: 'Personalised cognitive health guide for your dog' },
      { id: 'receive-mfy-results', icon: FlaskConical, text: "Your dog's individual longitudinal score report at trial close" },
      { id: 'receive-mfy-support', icon: Stethoscope, text: 'Direct access to your vet throughout the trial period' },
    ],
    timeline: [
      { id: 'tl-mfy-baseline', week: 'Week 0', label: 'Baseline Assessment', desc: 'Initial behavioural questionnaire and vet check-in' },
      { id: 'tl-mfy-4wk', week: 'Week 4', label: '4-Week Follow-Up', desc: 'Mid-trial behavioural questionnaire' },
      { id: 'tl-mfy-8wk', week: 'Week 8', label: '8-Week Follow-Up', desc: 'Final assessment and composite score report' },
    ],
    color: 'primary',
    spots: 6,
  },
  {
    id: 'trial-cns-002',
    code: 'CNS-002',
    name: 'Golden Years Mobility',
    tagline: 'Investigating CNS joint support formulas in senior dogs with reduced mobility',
    status: 'upcoming',
    phase: 'Phase 1',
    enrolled: 0,
    capacity: 40,
    duration: '12 weeks',
    startDate: 'September 2026',
    endDate: 'December 2026',
    sponsor: 'Clinigol Nutrition Solutions',
    leadVet: 'Dr. Aisha Nkemdirim, MRCVS',
    location: 'UK-wide (remote participation)',
    description: 'Golden Years Mobility will evaluate the impact of a CNS joint and mobility supplement on gait scores, owner-reported activity levels, and veterinary mobility assessments in dogs aged 7 and over with clinically confirmed reduced mobility.',
    eligibility: [
      { id: 'elig-cns-age', text: 'Dog aged 7 years or older', met: true },
      { id: 'elig-cns-mobility', text: 'Veterinarian-confirmed reduced mobility or joint stiffness', met: true },
      { id: 'elig-cns-meds', text: 'Not currently receiving NSAIDs or prescribed joint medication', met: true },
      { id: 'elig-cns-vet', text: 'Must be registered with a participating Clinigol vet', met: true },
    ],
    whatYoullReceive: [
      { id: 'receive-cns-supp', icon: Star, text: '12-week supply of CNS joint support supplement (complimentary)' },
      { id: 'receive-cns-report', icon: FlaskConical, text: 'Individual mobility score report at trial close' },
      { id: 'receive-cns-guide', icon: BookOpen, text: 'Senior dog mobility and exercise guide' },
    ],
    timeline: [
      { id: 'tl-cns-baseline', week: 'Week 0', label: 'Baseline Assessment', desc: 'Vet mobility scoring and owner questionnaire' },
      { id: 'tl-cns-4wk', week: 'Week 4', label: '4-Week Check-In', desc: 'Owner activity log review' },
      { id: 'tl-cns-8wk', week: 'Week 8', label: '8-Week Assessment', desc: 'Mid-trial vet mobility re-score' },
      { id: 'tl-cns-12wk', week: 'Week 12', label: 'Final Assessment', desc: 'Full mobility evaluation and report' },
    ],
    color: 'accent',
    spots: 40,
  },
];

export default function CurrentTrialsContent() {
  const [expandedTrial, setExpandedTrial] = useState<string | null>('trial-mfy-001');
  const [selectedTrial, setSelectedTrial] = useState<Trial | null>(null);

  return (
    <div className="min-h-screen" style={{ background: '#f8f7f9' }}>
      {/* Public nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-sm border-b" style={{ background: 'rgba(255,255,255,0.97)', borderColor: '#e8e0eb' }}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-2.5">
            <ClinigolLogo width={110} height={36} color="#681f77" />
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/sign-up-login" className="text-sm font-medium hidden sm:flex px-3 py-2 rounded-lg transition-colors hover:bg-purple-50" style={{ color: '#555' }}>Sign In</Link>
            <Link href="/sign-up-login" className="btn-primary text-sm">Join a Trial</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="border-b" style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f8f7f9 60%, #fff 100%)', borderColor: '#e8e0eb' }}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#4ade80' }} />
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#16a34a' }}>Actively Recruiting</span>
            </div>
            <h1 className="text-hero-xl font-bold mb-4" style={{ color: '#1e1e1e' }}>
              Current Clinigol Trials
            </h1>
            <p className="text-base leading-relaxed mb-6 max-w-xl" style={{ color: '#555' }}>
              We know how hard it can be to watch your dog age. Our clinical trials give you access to evidence-based nutritional interventions — and your participation helps build the science that supports every dog that follows.
            </p>
            <div className="flex flex-wrap gap-4 text-sm" style={{ color: '#666' }}>
              <div className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" style={{ color: '#16a34a' }} /> No charge to participate</div>
              <div className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" style={{ color: '#16a34a' }} /> UK-wide, remote participation</div>
              <div className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" style={{ color: '#16a34a' }} /> Supplements provided</div>
            </div>
          </div>
        </div>
      </div>

      {/* Trials */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-12 space-y-8">
        {trials.map((trial) => (
          <div key={trial.id} className="overflow-hidden rounded-2xl border transition-all duration-200 hover:shadow-lg" style={{ background: '#ffffff', borderColor: '#e8e0eb' }}>
            {/* Trial header */}
            <div className="p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-xs font-mono font-bold px-2.5 py-1 rounded-md" style={{ color: '#681f77', background: 'rgba(104,31,119,0.08)' }}>{trial.code}</span>
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={trial.status === 'recruiting'
                        ? { background: 'rgba(22,163,74,0.1)', color: '#16a34a' }
                        : { background: 'rgba(251,191,36,0.1)', color: '#d97706' }
                      }
                    >
                      {trial.status === 'recruiting' ? 'Recruiting Now' : `Opens ${trial.startDate}`}
                    </span>
                    <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: '#f3f0f5', color: '#666' }}>{trial.phase}</span>
                  </div>
                  <h2 className="text-xl font-bold mb-1" style={{ color: '#1e1e1e' }}>{trial.name}</h2>
                  <p className="text-sm mb-4" style={{ color: '#777' }}>{trial.tagline}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#444' }}>{trial.description}</p>
                </div>

                {/* Meta sidebar */}
                <div className="lg:w-64 flex-shrink-0 space-y-3">
                  <div className="rounded-xl p-4 space-y-3 text-xs" style={{ background: '#f8f5fa', border: '1px solid #ede5f0' }}>
                    <div className="flex items-center gap-2" style={{ color: '#666' }}>
                      <Users className="w-3.5 h-3.5" />
                      <span>{trial.enrolled}/{trial.capacity} enrolled</span>
                      {trial.spots > 0 && trial.spots <= 10 && (
                        <span className="font-semibold" style={{ color: '#d97706' }}>— {trial.spots} spots left</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2" style={{ color: '#666' }}>
                      <Clock className="w-3.5 h-3.5" />
                      <span>{trial.duration} trial period</span>
                    </div>
                    <div className="flex items-center gap-2" style={{ color: '#666' }}>
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{trial.startDate} – {trial.endDate}</span>
                    </div>
                    <div className="flex items-center gap-2" style={{ color: '#666' }}>
                      <Globe className="w-3.5 h-3.5" />
                      <span>{trial.location}</span>
                    </div>
                    <div className="flex items-start gap-2" style={{ color: '#666' }}>
                      <Stethoscope className="w-3.5 h-3.5 mt-0.5" />
                      <span>{trial.leadVet}</span>
                    </div>
                  </div>

                  {/* Enrollment bar */}
                  <div>
                    <div className="flex justify-between text-xs mb-1" style={{ color: '#888' }}>
                      <span>Enrollment</span>
                      <span>{Math.round((trial.enrolled / trial.capacity) * 100)}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: '#ede5f0' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${(trial.enrolled / trial.capacity) * 100}%`, background: '#681f77' }}
                      />
                    </div>
                  </div>

                  {trial.status === 'recruiting' ? (
                    <button
                      onClick={() => setSelectedTrial(trial)}
                      className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                    >
                      Request to Join <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button className="w-full text-sm px-5 py-2.5 rounded-xl font-semibold border transition-all duration-150" style={{ background: '#f8f5fa', borderColor: '#ddd0e5', color: '#681f77' }}>Register Interest</button>
                  )}
                </div>
              </div>

              {/* Expand toggle */}
              <button
                onClick={() => setExpandedTrial(expandedTrial === trial.id ? null : trial.id)}
                className="flex items-center gap-1.5 text-sm font-medium mt-5 hover:opacity-80 transition-opacity"
                style={{ color: '#681f77' }}
              >
                {expandedTrial === trial.id ? (
                  <><ChevronUp className="w-4 h-4" /> Hide details</>
                ) : (
                  <><ChevronDown className="w-4 h-4" /> Show eligibility, timeline &amp; what you&apos;ll receive</>
                )}
              </button>
            </div>

            {/* Expanded details */}
            {expandedTrial === trial.id && (
              <div className="border-t px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-8" style={{ borderColor: '#ede5f0', background: '#faf5ff' }}>
                {/* Eligibility */}
                <div>
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: '#1e1e1e' }}>
                    <Shield className="w-4 h-4" style={{ color: '#681f77' }} /> Eligibility criteria
                  </h3>
                  <ul className="space-y-2.5">
                    {trial.eligibility.map((item) => (
                      <li key={item.id} className="flex items-start gap-2.5 text-sm" style={{ color: '#555' }}>
                        <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: '#1e1e1e' }}>
                    <Calendar className="w-4 h-4" style={{ color: '#681f77' }} /> Trial timeline
                  </h3>
                  <div className="space-y-4">
                    {trial.timeline.map((tl, idx) => (
                      <div key={tl.id} className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white" style={{ background: '#681f77' }}>
                            {idx + 1}
                          </div>
                          {idx < trial.timeline.length - 1 && (
                            <div className="w-px h-6 mt-1" style={{ background: '#ddd0e5' }} />
                          )}
                        </div>
                        <div className="pb-1">
                          <p className="text-xs font-semibold" style={{ color: '#681f77' }}>{tl.week}</p>
                          <p className="text-sm font-medium" style={{ color: '#1e1e1e' }}>{tl.label}</p>
                          <p className="text-xs" style={{ color: '#777' }}>{tl.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What you'll receive */}
                <div>
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: '#1e1e1e' }}>
                    <Dog className="w-4 h-4" style={{ color: '#681f77' }} /> What you&apos;ll receive
                  </h3>
                  <ul className="space-y-3">
                    {trial.whatYoullReceive.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <li key={item.id} className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(104,31,119,0.08)' }}>
                            <ItemIcon className="w-3.5 h-3.5" style={{ color: '#681f77' }} />
                          </div>
                          <span className="text-sm leading-relaxed" style={{ color: '#555' }}>{item.text}</span>
                        </li>
                      );
                    })}
                  </ul>

                  {trial.status === 'recruiting' && (
                    <button
                      onClick={() => setSelectedTrial(trial)}
                      className="btn-primary w-full mt-5 flex items-center justify-center gap-2 text-sm"
                    >
                      Request to Join <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* CNS sub-brand strip */}
        <div className="p-6 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center gap-4" style={{ background: 'linear-gradient(135deg, rgba(104,31,119,0.05) 0%, #fff 100%)', borderColor: 'rgba(104,31,119,0.2)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#681f77' }}>
            <FlaskConical className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold" style={{ color: '#1e1e1e' }}>Powered by Clinigol Nutrition Solutions (CNS)</p>
            <p className="text-xs mt-0.5" style={{ color: '#777' }}>All trials use proprietary CNS active ingredients, licensed to leading veterinary nutrition brands. Evidence-based. Independently reviewed.</p>
          </div>
          <Link href="/sign-up-login" className="text-sm flex-shrink-0 px-5 py-2.5 rounded-xl font-semibold border transition-all duration-150" style={{ background: '#fff', borderColor: '#ddd0e5', color: '#681f77' }}>Learn More</Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t mt-8" style={{ background: '#fff', borderColor: '#e8e0eb' }}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ color: '#888' }}>
          <div className="flex items-center gap-2">
            <ClinigolLogo width={80} height={26} color="#aaa" />
            <span>© 2026 Clinigol Ltd · clinigol.vet · clinigol.pet</span>
          </div>
          <div className="flex gap-4">
            <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-foreground transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>

      {/* Request to join modal */}
      {selectedTrial !== null && (
        <RequestToJoinModal
          trial={selectedTrial}
          onClose={() => setSelectedTrial(null)}
        />
      )}
    </div>
  );
}